import { t } from "ttag";

import { Ellipsified } from "metabase/common/components/Ellipsified";
import { useSetting } from "metabase/common/hooks";
import { useTranslateContent } from "metabase/i18n/hooks";
import { ParameterFieldWidgetValue } from "metabase/parameters/components/widgets/ParameterFieldWidget/ParameterFieldWidgetValue/ParameterFieldWidgetValue";
import { formatParameterValue } from "metabase/parameters/utils/formatting";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import {
  getFields,
  hasFields,
  isFieldFilterUiParameter,
} from "metabase-lib/v1/parameters/utils/parameter-fields";
import { hasRemappedParameterValues } from "metabase-lib/v1/parameters/utils/parameter-source";
import {
  isBooleanParameter,
  isDateParameter,
  isStringParameter,
  isTemporalUnitParameter,
} from "metabase-lib/v1/parameters/utils/parameter-type";
import { parameterHasNoDisplayValue } from "metabase-lib/v1/parameters/utils/parameter-values";
import type {
  CardId,
  DashboardId,
  ParameterValue,
  RowValue,
} from "metabase-types/api";

export type FormattedParameterValueProps = {
  parameter: UiParameter;
  value: string | number | number[] | ParameterValue;
  cardId?: CardId;
  dashboardId?: DashboardId;
  placeholder?: string;
  isPopoverOpen?: boolean;
  dataTestId?: string;
};

function FormattedParameterValue({
  parameter,
  value,
  cardId,
  dashboardId,
  placeholder,
  isPopoverOpen = false,
}: FormattedParameterValueProps) {
  const tc = useTranslateContent();
  const formattingSettings = useSetting("custom-formatting");

  if (parameterHasNoDisplayValue(value)) {
    return (
      <Ellipsified showTooltip={!isPopoverOpen}>{placeholder}</Ellipsified>
    );
  }

  const first = getValue(value);
  const label = isBooleanParameter(parameter)
    ? getBooleanLabel(first as boolean)
    : undefined;

  const renderContent = () => {
    if (
      ((isFieldFilterUiParameter(parameter) && hasFields(parameter)) ||
        hasRemappedParameterValues(parameter, getFields(parameter))) &&
      !isDateParameter(parameter) &&
      !isTemporalUnitParameter(parameter)
    ) {
      return (
        <ParameterFieldWidgetValue
          fields={getFields(parameter)}
          value={value}
          parameter={parameter}
          cardId={cardId}
          dashboardId={dashboardId}
          displayValue={label}
        />
      );
    }

    if (label) {
      return (
        <span>
          {formatParameterValue(tc(label), parameter, formattingSettings)}
        </span>
      );
    }

    return (
      <span>
        {formatParameterValue(tc(value), parameter, formattingSettings)}
      </span>
    );
  };

  if (isStringParameter(parameter) || isDateParameter(parameter)) {
    const hasLongValue = typeof first === "string" && first.length > 80;
    return (
      <Ellipsified
        showTooltip={!isPopoverOpen}
        tooltipProps={{
          multiline: true,
          w: hasLongValue ? 450 : undefined,
        }}
      >
        {renderContent()}
      </Ellipsified>
    );
  }

  return renderContent();
}

function getValue(
  value: string | number | number[] | ParameterValue | undefined,
): RowValue | undefined {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value?.toString();
}

function getBooleanLabel(value: boolean) {
  return value ? t`True` : t`False`;
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default FormattedParameterValue;
